export interface NormalizedCjPids {
  byUserId: Record<number, string>
  bySegment: Record<string, string>
}

/** Pulls the PID row array from common API envelope shapes. */
const extractCjPidRows = (raw: unknown): unknown[] => {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'object') return []

  const o = raw as Record<string, unknown>

  // { data: [...] } or { data: [...], total }
  if (Array.isArray(o.data)) return o.data

  // { data: { data: [...], total } }
  const inner = o.data
  if (
    inner &&
    typeof inner === 'object' &&
    Array.isArray((inner as { data?: unknown }).data)
  ) {
    return (inner as { data: unknown[] }).data
  }

  return []
}

/**
 * Normalizes `/affiliate-networks/user-cj-pids` payloads into:
 *  - `bySegment`: PID per segment name like `'website'` (rows with `type === 'segment'`)
 *  - `byUserId`: kept in the shape for back-compat but always empty.
 *
 * Per-user CJ PID resolution is now done **backend-side** during link generation (the backend swaps in
 * each affiliate's dedicated, account-matched PID). The client must NOT build a user→PID map: it isn't
 * needed for resolution and would expose user→PID pairs (incl. the top-100 dynamic PIDs) to the browser.
 */
export const normalizeUserCjPidsResponse = (raw: unknown): NormalizedCjPids => {
  const rows = extractCjPidRows(raw)
  const bySegment: Record<string, string> = {}

  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const r = row as Record<string, unknown>

    const type = r.type
    const name = r.name
    const pid = r.pid
    const pidStr = pid == null ? '' : String(pid)
    if (!pidStr) continue

    if (type === 'segment' && typeof name === 'string' && name !== '') {
      bySegment[name] = pidStr
    }
  }

  return { byUserId: {}, bySegment }
}
